import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Select, Table, TextInput } from 'nhsuk-react-components';

const OpeningTimes = forwardRef((props, ref) => {
    const [error, setError] = useState(undefined);
    const [isValid, setIsValid] = useState(undefined);

    const validate = () => {
        const value = getValue();
        let overallValid = true;
        let overallError;

        for (const key in value) {
            const row = value[key];
            const { valid, error } = validateDay(row, key);

            if (!valid) {
                overallValid = false;
                overallError = error;
                break; // stop on first failure
            }
        }

        setIsValid(overallValid);
        setError(overallError);

        return overallValid;
    };

    useImperativeHandle(ref, () => ({
        validate,
        get isValid() {
            return isValid; // always up-to-date
        }
    }));

    const validateDay = (dayInputs, day) => {
        //let valid = true;
        var dayData = { ...dayInputs };
        let error = null;
        let valid = true;
        if (
            dayData.am_open === '' &&
            dayData.am_close === '' &&
            dayData.pm_open === '' &&
            dayData.pm_close === ''
        ) {
            error = `Please enter an opening and closing time, or set all to "Closed" - ${capitalise(
                day
            )}`;
            valid = false;
            return { valid, error };
        }
        // If every field is set to closed, its valid
        if (
            dayData.am_open === 'closed' &&
            dayData.am_close === 'closed' &&
            dayData.pm_open === 'closed' &&
            dayData.pm_close === 'closed'
        ) {
            return { valid: true, error: null };
        }

        if (
            [dayData.am_open, dayData.am_close, dayData.pm_open, dayData.pm_close].filter(
                v => v != null && v !== ''
            ).length === 1
        ) {
            error = `At least two fields must be selected for ${capitalise(day)}`;
            valid = false;
            return { valid, error };
        }
        //Makes sure if both AMs or PMs are selected, then the remaining fields are set to Closed
        if (
            dayData.am_open !== '' &&
            dayData.am_open !== 'closed' &&
            dayData.pm_close !== '' &&
            dayData.pm_close !== 'closed'
        ) {
            if (dayData.am_close === 'closed' || dayData.pm_open === 'closed') {
                return {
                    valid: false,
                    error: `Invalid 'Closed' fields selected - ${capitalise(day)}`
                };
            }
        }

        if (dayData.am_open !== '' && dayData.am_close !== '') {
            if (dayData.pm_close === '' || dayData.pm_open === '') {
                return {
                    valid: false,
                    error: `Please do not leave fields blank, if you are not open, select 'Closed' - ${capitalise(
                        day
                    )}`
                };
            }
        }

        if (dayData.pm_open !== '' && dayData.pm_close !== '') {
            if (dayData.am_close === '' || dayData.am_open === '') {
                return {
                    valid: false,
                    error: `Please do not leave fields blank, if you are not open, select 'Closed' - ${capitalise(
                        day
                    )}`
                };
            }
        }
        ['am_open', 'am_close', 'pm_open', 'pm_close'].forEach(key => {
            if (dayData[key] === 'closed') {
                dayData[key] = '';
            }
        });

        // If AM Open has a selected time
        if (dayData.am_open !== '') {
            // If no closing time is selected
            if (dayData.am_close === '' && dayData.pm_close === '') {
                return {
                    valid: false,
                    error: `Please select a close time - ${capitalise(day)}`
                };
            }
            // If only AM open and PM close are selected
            if (dayData.am_close === '' && dayData.pm_open === '' && dayData.pm_close !== '') {
                // If the closing time is less than or equal to the opening time
                if (dayData.pm_close <= dayData.am_open) {
                    return {
                        valid: false,
                        error: `Closing time must be later than opening time - ${capitalise(day)}`
                    };
                }
            }
        }

        // If AM Close has a selected time
        if (dayData.am_close !== '') {
            // There has to be an AM open in order for there to be an AM close
            if (dayData.am_open === '') {
                return {
                    valid: false,
                    error: `Please select an AM opening time - ${capitalise(day)}`
                };
            }
            // If AM Open is greater than or equal to AM Close
            if (dayData.am_open >= dayData.am_close) {
                return {
                    valid: false,
                    error: `AM Close must be later than AM Open - ${capitalise(day)}`
                };
            }
        }

        // If PM Open has a selected time
        if (dayData.pm_open !== '') {
            // There has to be an PM close in order for there to be an PM open
            if (dayData.pm_close === '') {
                return {
                    valid: false,
                    error: `Please select a PM closing time - ${capitalise(day)}`
                };
            }
            // If PM Open is greater than or eqaul to PM Close
            if (dayData.pm_open >= dayData.pm_close) {
                return {
                    valid: false,
                    error: `PM Close must be later than PM Open - ${capitalise(day)}`
                };
            }
            // If an AM Close is selected we need to make sure that the selected PM Open is greater
            if (dayData.am_close !== '') {
                if (dayData.pm_open < dayData.am_close) {
                    return {
                        valid: false,
                        error: `PM Open must be later or equal to AM Close - ${capitalise(day)}`
                    };
                }
            }
            // This makes sure that if a PM Open is selected as well as an AM Open, then there has to be an AM Close selected
            if (dayData.am_open !== '' && dayData.am_close === '') {
                return {
                    valid: false,
                    error: `Please select an AM closing time - ${capitalise(day)}`
                };
            }
        }

        // If PM Close has a selected time
        if (dayData.pm_close !== '') {
            // If an AM Close time has been selected but a PM Open hasnt
            if (dayData.am_close !== '' && dayData.pm_open === '') {
                return {
                    valid: false,
                    error: `Please select a PM opening time - ${capitalise(day)}`
                };
            }
            // If no other field has been selected
            if (dayData.am_open === '' && dayData.am_close === '' && dayData.pm_open === '') {
                return {
                    valid: false,
                    error: `Please select an opening time - ${capitalise(day)}`
                };
            }
        }

        return { valid, error };
    };

    function capitalise(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    const handleChange = async (event, day, key) => {
        const value = getValue();
        value[day][key] = event.target.value;
        await props.onChange({
            questionId: props.question.id,
            value: value
        });
        validate();
        validateDay(value[day], day);
    };

    const getValue = () => {
        const defaults = {
            monday: { am_open: '', am_close: '', pm_open: '', pm_close: '', comments: '' },
            tuesday: { am_open: '', am_close: '', pm_open: '', pm_close: '', comments: '' },
            wednesday: { am_open: '', am_close: '', pm_open: '', pm_close: '', comments: '' },
            thursday: { am_open: '', am_close: '', pm_open: '', pm_close: '', comments: '' },
            friday: { am_open: '', am_close: '', pm_open: '', pm_close: '', comments: '' },
            saturday: { am_open: '', am_close: '', pm_open: '', pm_close: '', comments: '' },
            sunday: { am_open: '', am_close: '', pm_open: '', pm_close: '', comments: '' }
        };

        const validOptions = {
            am_open: amOpen,
            am_close: amClose,
            pm_open: pmOpen,
            pm_close: pmClose
        };

        const input = props.question.value ?? defaults;

        // validate each day/field
        const fixed = Object.fromEntries(
            Object.entries(input).map(([day, values]) => {
                const cleaned = Object.fromEntries(
                    Object.entries(values).map(([field, val]) => {
                        if (field in validOptions) {
                            return [field, validOptions[field].includes(val) ? val : ''];
                        }
                        return [field, val]; // comments don't need validation
                    })
                );
                return [day, cleaned];
            })
        );

        return fixed;
    };

    const days = [
        { key: 'monday', display: 'Monday' },
        { key: 'tuesday', display: 'Tuesday' },
        { key: 'wednesday', display: 'Wednesday' },
        { key: 'thursday', display: 'Thursday' },
        { key: 'friday', display: 'Friday' },
        { key: 'saturday', display: 'Saturday' },
        { key: 'sunday', display: 'Sunday' }
    ];

    const amOpenSelectItems = selectOptions(amOpen);
    const amCloseSelectItems = selectOptions(amClose);
    const pmOpenSelectItems = selectOptions(pmOpen);
    const pmCloseSelectItems = selectOptions(pmClose);
    //const allDaySelectItems = selectOptions(time24);

    function selectOptions(items) {
        return items.map((item, index) => (
            <Select.Option key={index} value={item}>
                {item === 'closed' ? 'Closed' : item}
            </Select.Option>
        ));
    }

    const inputStyle = { width: '300px' };
    const selectStyle = { width: '100px' };

    return (
        <div className={`nhsuk-form-group ${isValid === false ? 'nhsuk-form-group--error' : ''}`}>
            <label className="nhsuk-label">{props.question.name}</label>
            <span className="nhsuk-hint">{props.question.hint_text}</span>

            {isValid === false && (
                <span className="nhsuk-error-message" role="alert">
                    {error}
                </span>
            )}

            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Cell></Table.Cell>
                        <Table.Cell id="am-open-label">AM Open</Table.Cell>
                        <Table.Cell id="am-close-label">AM Close</Table.Cell>
                        <Table.Cell id="pm-open-label">PM Open</Table.Cell>
                        <Table.Cell id="pm-close-label">PM Close</Table.Cell>
                        <Table.Cell id="comments-label">Comments</Table.Cell>
                    </Table.Row>
                </Table.Head>
                <Table.Body>
                    {days.map((day, index) => {
                        const dayValue = getValue()[day.key];

                        return (
                            <Table.Row key={index}>
                                <Table.Cell>{day.display}</Table.Cell>
                                <Table.Cell>
                                    <Select
                                        value={dayValue.am_open}
                                        onChange={event => handleChange(event, day.key, 'am_open')}
                                        onBlur={event => handleChange(event, day.key, 'am_open')}
                                        style={selectStyle}
                                        aria-labelledby="am-open-label"
                                    >
                                        {amOpenSelectItems}
                                    </Select>
                                </Table.Cell>
                                <Table.Cell>
                                    <Select
                                        value={dayValue.am_close}
                                        onChange={event => handleChange(event, day.key, 'am_close')}
                                        onBlur={event => handleChange(event, day.key, 'am_close')}
                                        style={selectStyle}
                                        aria-labelledby="am-close-label"
                                    >
                                        {amCloseSelectItems}
                                    </Select>
                                </Table.Cell>
                                <Table.Cell>
                                    <Select
                                        value={dayValue.pm_open}
                                        onChange={event => handleChange(event, day.key, 'pm_open')}
                                        onBlur={event => handleChange(event, day.key, 'pm_open')}
                                        style={selectStyle}
                                        aria-labelledby="pm-open-label"
                                    >
                                        {pmOpenSelectItems}
                                    </Select>
                                </Table.Cell>
                                <Table.Cell>
                                    <Select
                                        value={dayValue.pm_close}
                                        onChange={event => handleChange(event, day.key, 'pm_close')}
                                        onBlur={event => handleChange(event, day.key, 'pm_close')}
                                        style={selectStyle}
                                        aria-labelledby="pm-close-label"
                                    >
                                        {pmCloseSelectItems}
                                    </Select>
                                </Table.Cell>
                                <Table.Cell>
                                    <TextInput
                                        className="comments"
                                        type="text"
                                        value={dayValue.comments}
                                        onChange={event => handleChange(event, day.key, 'comments')}
                                        onBlur={event => handleChange(event, day.key, 'comments')}
                                        style={inputStyle}
                                        aria-labelledby="comments-label"
                                    />
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
        </div>
    );
});

const amOpen = [
    '',
    'closed',
    '00:00',
    '00:15',
    '00:30',
    '00:45',
    '01:00',
    '01:15',
    '01:30',
    '01:45',
    '02:00',
    '02:15',
    '02:30',
    '02:45',
    '03:00',
    '03:15',
    '03:30',
    '03:45',
    '04:00',
    '04:15',
    '04:30',
    '04:45',
    '05:00',
    '05:15',
    '05:30',
    '05:45',
    '06:00',
    '06:15',
    '06:30',
    '06:45',
    '07:00',
    '07:15',
    '07:30',
    '07:45',
    '08:00',
    '08:15',
    '08:30',
    '08:45',
    '09:00',
    '09:15',
    '09:30',
    '09:45',
    '10:00',
    '10:15',
    '10:30',
    '10:45',
    '11:00',
    '11:15',
    '11:30',
    '11:45',
    '12:00'
];

const amClose = [
    '',
    'closed',
    '00:00',
    '00:15',
    '00:30',
    '00:45',
    '01:00',
    '01:15',
    '01:30',
    '01:45',
    '02:00',
    '02:15',
    '02:30',
    '02:45',
    '03:00',
    '03:15',
    '03:30',
    '03:45',
    '04:00',
    '04:15',
    '04:30',
    '04:45',
    '05:00',
    '05:15',
    '05:30',
    '05:45',
    '06:00',
    '06:15',
    '06:30',
    '06:45',
    '07:00',
    '07:15',
    '07:30',
    '07:45',
    '08:00',
    '08:15',
    '08:30',
    '08:45',
    '09:00',
    '09:15',
    '09:30',
    '09:45',
    '10:00',
    '10:15',
    '10:30',
    '10:45',
    '11:00',
    '11:15',
    '11:30',
    '11:45',
    '12:00',
    '12:15',
    '12:30',
    '12:45',
    '13:00',
    '13:15',
    '13:30',
    '13:45',
    '14:00',
    '14:15',
    '14:30',
    '14:45',
    '15:00'
];

const pmOpen = [
    '',
    'closed',
    '12:00',
    '12:15',
    '12:30',
    '12:45',
    '13:00',
    '13:15',
    '13:30',
    '13:45',
    '14:00',
    '14:15',
    '14:30',
    '14:45',
    '15:00',
    '15:15',
    '15:30',
    '15:45',
    '16:00'
];

const pmClose = [
    '',
    'closed',
    '12:00',
    '12:15',
    '12:30',
    '12:45',
    '13:00',
    '13:15',
    '13:30',
    '13:45',
    '14:00',
    '14:15',
    '14:30',
    '14:45',
    '15:00',
    '15:15',
    '15:30',
    '15:45',
    '16:00',
    '16:15',
    '16:30',
    '16:45',
    '17:00',
    '17:15',
    '17:30',
    '17:45',
    '18:00',
    '18:15',
    '18:30',
    '18:45',
    '19:00',
    '19:15',
    '19:30',
    '19:45',
    '20:00',
    '20:15',
    '20:30',
    '20:45',
    '21:00',
    '21:15',
    '21:30',
    '21:45',
    '22:00',
    '22:15',
    '22:30',
    '22:45',
    '23:00',
    '23:15',
    '23:30',
    '23:45',
    '00:00'
];

export default OpeningTimes;
